/*
 * Copyright (c) CIRROM 2025.
 */

import React, { useEffect, useMemo } from "react";
import { ThemeProvider as MuiThemeProvider } from "@mui/material/styles";
import { THEMES } from "./constants";
import { createTheme } from "./theme";
import {
  provideGlobalGridOptions,
  themeQuartz,
  colorSchemeDarkBlue,
} from "ag-grid-community";
import { getCspNonce } from "../../../utils/cspNonce";

const initialState = {
  theme: THEMES.CIRROM,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  setTheme: (theme: string) => {},
};
export const ThemeContext = React.createContext(initialState);

type ThemeProviderProps = {
  children: React.ReactNode;
};

export function ThemeProvider({ children }: ThemeProviderProps) {
  const [theme, _setTheme] = React.useState<string>(initialState.theme);

  useEffect(() => {
    const storedTheme = localStorage.getItem("theme");

    if (storedTheme) {
      _setTheme(JSON.parse(storedTheme));
    }
  }, []);

  const setTheme = (theme: string) => {
    localStorage.setItem("theme", JSON.stringify(theme));
    _setTheme(theme);
    // Reload the page to apply AG Grid theme changes
    window.location.reload();
  };

  useEffect(() => {
    // AG Grid's theming API writes <style> elements into the document, so it needs the
    // same nonce emotion gets; undefined here leaves it behaving exactly as before.
    // loadThemeGoogleFonts stays off explicitly: themeQuartz names IBM Plex Sans as a
    // googleFont, and letting it fetch would reopen the CSP hole the self-hosted fonts
    // closed. Grids fall back to the -apple-system stack, as they already do.
    const styleNonce = getCspNonce();

    if (theme === THEMES.DARK) {
      const darkTheme = themeQuartz.withPart(colorSchemeDarkBlue).withParams({
        spacing: 6,
      });
      provideGlobalGridOptions({
        theme: darkTheme,
        styleNonce,
        loadThemeGoogleFonts: false,
      });
    } else {
      const lightTheme = themeQuartz.withParams({
        spacing: 6,
      });
      provideGlobalGridOptions({
        theme: lightTheme,
        styleNonce,
        loadThemeGoogleFonts: false,
      });
    }
  }, [theme]);

  const muiTheme = useMemo(() => createTheme(theme), [theme]);

  return (
    <ThemeContext.Provider value={{ theme, setTheme }}>
      <MuiThemeProvider theme={muiTheme}>{children}</MuiThemeProvider>
    </ThemeContext.Provider>
  );
}
